import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import CardView from "../../components/molecules/CardView/CardView";
import Button from "../../components/atoms/Button/Button";
import DataTable from "../../components/organisms/DataTable/DataTable";
import AnimatedCard from "../../components/molecules/AnimatedCard/AnimatedCard";
import Input from "../../components/atoms/Input/Input";
import TextArea from "../../components/atoms/TextArea/TextArea";
import ValidationMessage from "../../components/atoms/ValidationMessage/ValidationMessage";
import { serviceTypeBusiness } from "../../services/typeBusinnes";
import { validateData } from "../../utiles/ValidateEntries";
import ConfirmDialog from "../../components/molecules/ConfirmDialog/ConfirmDialog";
import "./TypeBusiness.scss";

const TypeBusiness = () => {
    const [typeBusinessList, setTypeBusinessList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
    });
    const [emptyField, setEmptyField] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        itemToDelete: null,
    });

    // Definir las columnas de la tabla
    const columns = [
        {
            field: "name",
            header: "Tipo de negocio",
            sortable: true,
        },
        {
            field: "description",
            header: "Descripción",
            sortable: true,
        },
    ];

    // Obtener tipos de negocio
    const fetchTypesBusiness = async () => {
        setLoading(true);
        try {
            const response = await serviceTypeBusiness.getAll();
            if (response.success) {
                setTypeBusinessList(response.data.data || response.data || []);
            } else {
                toast.error("Error", {
                    description: response.message,
                    position: "top-right",
                });
            }
        } catch (error) {
            console.error("Error al obtener tipos de negocio:", error);
            toast.error("Error", {
                description: "No se pudieron cargar los tipos de negocio",
                position: "top-right",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTypesBusiness();
    }, []);

    // Manejar cambios en el formulario
    const handleChangeData = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEmptyField((prev) => ({
            ...prev,
            [name]: false,
        }));
    };

    // Abrir modal para crear
    const handleOpenCreate = () => {
        setEditingType(null);
        setFormData({
            name: "",
            description: "",
        });
        setEmptyField({});
        setIsModalOpen(true);
    };

    // Abrir modal para editar
    const handleEdit = (rowData) => {
        setEditingType(rowData);
        setFormData({
            name: rowData.name,
            description: rowData.description,
        });
        setEmptyField({});
        setIsModalOpen(true);
    };

    // Eliminar tipo de negocio
    const handleDelete = (rowData) => {
        setConfirmDialog({
            isOpen: true,
            itemToDelete: rowData,
        });
    };

    const confirmDelete = async () => {
        const rowData = confirmDialog.itemToDelete;
        if (!rowData) return;

        const toastId = toast.loading("Eliminando...");

        try {
            const response = await serviceTypeBusiness.delete(rowData.id);

            if (response.success) {
                toast.success("¡Éxito!", {
                    description: response.message,
                    position: "top-right",
                    icon: "🎉",
                    id: toastId,
                });

                // Recargar lista
                fetchTypesBusiness();
            } else {
                toast.error("Error", {
                    description: response.message,
                    position: "top-right",
                    icon: "😞",
                    id: toastId,
                });
            }
        } catch (error) {
            toast.error("Error", {
                description: "No se pudo eliminar el tipo de negocio",
                position: "top-right",
                icon: "😞",
                id: toastId,
            });
        }
    };

    // Enviar formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        const { status, emptyFields } = validateData(formData);
        setEmptyField(emptyFields);

        if (!status) {
            console.error("Por favor, completa todos los campos requeridos.");
            return;
        }

        const toastId = toast.loading("Procesando...");

        try {
            let response;

            if (editingType) {
                // Actualizar
                response = await serviceTypeBusiness.update(
                    editingType.id,
                    formData
                );
            } else {
                // Crear
                response = await serviceTypeBusiness.create(formData);
            }

            if (response.success) {
                toast.success("¡Éxito!", {
                    description: response.message,
                    position: "top-right",
                    icon: "🎉",
                    id: toastId,
                });

                // Recargar lista
                fetchTypesBusiness();

                // Cerrar modal después de 1 segundo
                setTimeout(() => setIsModalOpen(false), 1000);
            } else {
                toast.error("¡Ups...!", {
                    description:
                        response.message || "Error. Inténtalo más tarde.",
                    position: "top-right",
                    icon: "😞",
                    id: toastId,
                });
            }
        } catch (error) {
            console.error("Ocurrió un error en el submit:", error);
            toast.error("Error", {
                description: "Ocurrió un error inesperado",
                position: "top-right",
                icon: "😞",
                id: toastId,
            });
        }
    };

    return (
        <CardView>
            <div className="type-business">
                <div className="type-business__header">
                    <h1 className="type-business__title">Tipos de Negocio</h1>
                    <Button
                        text={"Agregar tipo de negocio"}
                        onClick={handleOpenCreate}
                        className="type-business__addBtn degrad"
                    />
                </div>

                <DataTable
                    data={typeBusinessList}
                    columns={columns}
                    loading={loading}
                    showActions={true}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    emptyMessage="No hay tipos de negocio registrados"
                    striped={true}
                    hoverable={true}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    responsiveLayout="scroll"
                />

                {isModalOpen && (
                    <AnimatedCard
                        setClose={setIsModalOpen}
                        title={
                            editingType
                                ? "Editar Tipo de Negocio"
                                : "Crear Tipo de Negocio"
                        }
                        size="md"
                        variant="elegant"
                        coloredHeader={true}
                    >
                        <form
                            onSubmit={handleSubmit}
                            className="type-business__form"
                        >
                            <div className="type-business__form-group">
                                <ValidationMessage show={emptyField.name}>
                                    <Input
                                        text="Tipo de negocio"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChangeData}
                                        placeholder="Nombre del tipo de negocio"
                                        show={emptyField.name}
                                    />
                                </ValidationMessage>
                            </div>

                            <div className="type-business__form-group">
                                <ValidationMessage
                                    show={emptyField.description}
                                >
                                    <TextArea
                                        text="Descripción"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChangeData}
                                        placeholder="Descripción del tipo de negocio"
                                        show={emptyField.description}
                                    />
                                </ValidationMessage>
                            </div>

                            <div className="type-business__form-actions">
                                <Button
                                    text={"Cancelar"}
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                />
                                <Button
                                    text={
                                        editingType ? "Actualizar" : "Guardar"
                                    }
                                    type="submit"
                                    className="degrad"
                                />
                            </div>
                        </form>
                    </AnimatedCard>
                )}

                <ConfirmDialog
                    isOpen={confirmDialog.isOpen}
                    onClose={() =>
                        setConfirmDialog({ isOpen: false, itemToDelete: null })
                    }
                    onConfirm={confirmDelete}
                    title="Eliminar Tipo de Negocio"
                    message="¿Estás seguro de que deseas eliminar este tipo de negocio?"
                    itemName={confirmDialog.itemToDelete?.name}
                    confirmText="Eliminar"
                    cancelText="Cancelar"
                    type="danger"
                />
            </div>
        </CardView>
    );
};

export default TypeBusiness;
